<template>
  <v-container fluid>

    <!-- Search bar with shadow page links -->
    <div class="pt-3 pb-1">
      <div class="field-label text-left pl-4">
        {{ $t('dash.dictionary.search') }}
      </div>
      <div class="d-flex align-center search-row">
        <v-text-field
          v-model="searchQuery"
          append-icon="mdi-magnify"
          outlined
          dense
          hide-details
          clearable
          class="flex-grow-1 search-field"
        />
        <!-- Shadow links: pink to stand out from the teal scope chips below -->
        <div class="d-flex align-center ml-2 flex-shrink-0 shadow-links">
          <v-btn
            small
            outlined
            color="#E05484"
            class="shadow-link-btn mr-1"
            @click="redirect('/dashboard/dictionary/shadow')"
          >
            {{ $t('dash.shadowDictionary.title') }}
          </v-btn>
          <v-btn
            small
            outlined
            color="#E05484"
            class="shadow-link-btn"
            @click="redirect('/dashboard/dictionary/shadow-tags')"
          >
            {{ $t('dash.shadowTags.title') }}
          </v-btn>
        </div>
      </div>

      <!-- Search scope chips -->
      <div class="d-flex align-center scope-row mt-2 pl-4">
        <v-chip
          v-for="opt in scopeOptions"
          :key="opt.value"
          x-small
          :color="searchScope === opt.value ? '#00A9B3' : ''"
          :outlined="searchScope !== opt.value"
          :dark="searchScope === opt.value"
          class="scope-chip mr-1 mb-1"
          @click="searchScope = opt.value"
        >
          {{ opt.label }}
        </v-chip>
      </div>
    </div>

    <!-- Add new category (admin only — page is admin-gated, kept for clarity) -->
    <DetailListItem
      v-if="$store.getters.user.role === 'a'"
      @click.native="$modal.show('add-category-modal')"
      :highlighted="true"
      icon-right
      icon="plus-pink.svg"
    >{{ $t("dash.dictionary.category.add") }}</DetailListItem>

    <!-- No results state -->
    <div
      v-if="categories.length > 0 && filteredCategories.length === 0"
      class="pa-4 text-center grey--text"
    >
      {{ $t('dash.dictionary.noResults') }}
    </div>

    <div
      v-for="category in filteredCategories"
      :key="category.id"
    >
      <DetailListItem
        @click.native="collapseContent(`category${category.id}`)"
        icon-right
        icon="arrow-down-pink.svg"
        height="auto"
      >
        <div class="category-row">
          <span class="category-title">{{ category.title }}</span>
          <!-- Visible menu button replaces the previous long-press -->
          <v-btn
            v-if="category.editable"
            icon
            x-small
            color="#00A9B3"
            class="category-menu-btn ml-1"
            @click.stop="$modal.show(`category${category.id}-menu-modal`)"
          >
            <v-icon small>mdi-dots-horizontal</v-icon>
          </v-btn>
        </div>
      </DetailListItem>

      <div
        :id="`category${category.id}-content`"
        class="content-closed listitem-content"
      >
        <DetailListItem
          v-if="$store.getters.user.role === 'a'"
          @click.native="
            $store.commit('setCategoryId', category.id);
            redirect(`/dashboard/dictionary/create?categoryId=${category.id}`);
          "
          :highlighted="true"
          icon-right
          icon="plus-pink.svg"
        >{{ $t("dash.dictionary.article.add") }}</DetailListItem>

        <div v-for="entry in entriesForCategory(category)" :key="entry.id">
          <DetailListItem
            @click.native="collapseContent(`entry${entry.id}`)"
            height="auto"
            icon-right
            icon="arrow-down-pink.svg"
          >{{ entry.title }}</DetailListItem>

          <div :id="`entry${entry.id}-content`" class="content-closed">
            <div class="entry-content">
              <div>
                <p>{{ entry.description }}</p>
                <div class="uploads">
                  <div
                    v-for="upload in entry.uploads"
                    :key="upload.id"
                    class="upload-container"
                  >
                    <video
                      controls
                      v-if="upload.mimeType.includes('video/')"
                      :src="upload.blobUri"
                      :alt="upload.originalname"
                    />
                    <auto-rotate v-else>
                      <img
                        :src="upload.blobUri"
                        :alt="upload.originalname"
                      />
                    </auto-rotate>
                  </div>
                </div>
                <div v-if="entry.tags.length > 0" class="tags">
                  <span
                    v-for="tag in entry.tags"
                    :key="tag.id"
                    :class="['tag', entry.editable && 'remove-tag']"
                    @click="entry.editable && removeTag(entry.id, tag)"
                  >#{{ tag.name }}</span>
                </div>
              </div>
              <div class="entry-actions">
                <Button
                  v-if="$store.getters.user.role === 'a'"
                  color="#00A9B3"
                  border="1px solid #E05484"
                  bgcolor="transparent"
                  width="30px"
                  margin="0 0 10px 0"
                  padding="0"
                  icon="close-pink"
                  @click.native="deleteEntry(entry.id)"
                ></Button>
                <Button
                  v-if="$store.getters.user.role === 'a'"
                  color="#00A9B3"
                  border="1px solid #E05484"
                  bgcolor="transparent"
                  width="30px"
                  margin="10px 0"
                  padding="0"
                  icon="pen-pink"
                  @click.native="
                    $store.commit('editingEntry', {
                      title: entry.title,
                      description: entry.description,
                      categoryId: category.id,
                      entryId: entry.id,
                      addedTags: entry.tags,
                      uploads: entry.uploads
                    });
                    redirect(
                      `/dashboard/dictionary/create?entryId=${entry.id}&categoryId=${category.id}`
                    );
                  "
                ></Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <modals-container @category-edit="getCategories()" />

      <modal
        class="menu"
        :name="`category${category.id}-menu-modal`"
        width="100%"
        height="auto"
      >
        <ListItem
          border="1px solid #31b9be"
          @click.native="
            $modal.hide(`category${category.id}-menu-modal`);
            editCategory(category);
          "
        >{{ $t("dash.dictionary.category.edit") }}</ListItem>
        <ListItem
          border="1px solid #31b9be"
          last="true"
          @click.native="
            $modal.hide(`category${category.id}-menu-modal`);
            deleteCategory(category.id);
          "
        >{{ $t("dash.dictionary.category.delete") }}</ListItem>
      </modal>
    </div>

    <CategoryModal
      :title="$t('dash.dictionary.category.add')"
      @add-category="getCategories"
    />
  </v-container>
</template>

<script>
import { neoparentCategories, neoparentDictionaryEntries } from "@/api";
import Button from "@/page_elements/Button";
import InputField from "@/page_elements/InputField";

export default {
  name: "neoparent-lexicon",
  components: {
    InputField,
    Button
  },
  data() {
    return {
      categories: [],
      searchQuery: "",
      searchDebounced: "",
      // 'all' | 'title' | 'description' | 'category' | 'tag'
      searchScope: "all"
    };
  },
  watch: {
    searchQuery(val) {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        this.searchDebounced = val || "";
      }, 500);
    },
    searchDebounced() {
      this.$nextTick(() => this.syncCategoryExpansion());
    },
    searchScope() {
      this.$nextTick(() => this.syncCategoryExpansion());
    }
  },
  computed: {
    scopeOptions() {
      return [
        { value: "all",         label: this.$t("dash.dictionary.scopeAll") },
        { value: "title",       label: this.$t("dash.dictionary.scopeTitle") },
        { value: "description", label: this.$t("dash.dictionary.scopeDesc") },
        { value: "category",    label: this.$t("dash.dictionary.scopeCategory") },
        { value: "tag",         label: this.$t("dash.dictionary.scopeTag") }
      ];
    },
    normalizedQuery() {
      return (this.searchDebounced || "").trim().toLowerCase();
    },
    filteredCategories() {
      const q = this.normalizedQuery;
      if (!q) return this.categories;

      return this.categories.filter(cat => {
        if (this.categoryTitleMatches(cat, q)) return true;
        return (cat.dictionaryEntries || []).some(e => this.entryMatches(e, q));
      });
    }
  },
  mounted() {
    if (this.$store.getters.user.role !== "a") {
      this.redirect("/dashboard");
      return;
    }
    this.getCategories();
  },
  methods: {
    categoryTitleMatches(cat, q) {
      if (this.searchScope !== "all" && this.searchScope !== "category") return false;
      return String(cat.title || "").toLowerCase().includes(q);
    },
    entryMatches(entry, q) {
      const inTitle = String(entry.title || "").toLowerCase().includes(q);
      const inDesc  = String(entry.description || "").toLowerCase().includes(q);
      const inTag   = (entry.tags || []).some(t => String(t.name || "").toLowerCase().includes(q));

      switch (this.searchScope) {
        case "title":       return inTitle;
        case "description": return inDesc;
        case "tag":         return inTag;
        case "category":    return false;
        default:            return inTitle || inDesc || inTag;
      }
    },
    entriesForCategory(cat) {
      const q = this.normalizedQuery;
      const entries = cat.dictionaryEntries || [];
      if (!q) return entries;
      // Category-title hit shows the full set so users can browse it
      if (this.categoryTitleMatches(cat, q)) return entries;
      return entries.filter(e => this.entryMatches(e, q));
    },
    syncCategoryExpansion() {
      const q = this.normalizedQuery;
      this.categories.forEach(cat => {
        const el = document.getElementById(`category${cat.id}-content`);
        if (!el) return;
        // Empty query collapses everything; entry-match expands to surface results
        if (!q) {
          el.classList.add("content-closed");
          return;
        }
        const entryHit = (cat.dictionaryEntries || []).some(e => this.entryMatches(e, q));
        if (entryHit && !this.categoryTitleMatches(cat, q)) {
          el.classList.remove("content-closed");
        }
      });
    },
    getCategories() {
      neoparentCategories
        .getCategoriesWithEntries(this.editId)
        .then(result => {
          this.consoleLog(result);
          this.$store.commit("clearEntry");
          this.categories = result.data.categories;
        })
        .catch(err => {
          this.consoleLog(err);
        });
    },
    collapseContent(tag) {
      document
        .getElementById(`${tag}-content`)
        .classList.toggle("content-closed");
    },
    deleteEntry(id) {
      if (confirm(this.$t("dash.dictionary.article.confirm"))) {
        neoparentDictionaryEntries
          .deleteEntry(id)
          .then(result => {
            this.consoleLog(result);
            this.getCategories();
          })
          .catch(err => {
            this.consoleLog(err);
          });
      }
    },
    removeTag(entryId, tag) {
      if (confirm(`#${tag.name} ${this.$t("dash.dictionary.tags.confirm")}`)) {
        neoparentDictionaryEntries
          .removeTagFromEntry(entryId, tag.id)
          .then(result => {
            this.consoleLog(result);
            this.getCategories();
          })
          .catch(err => {
            this.consoleLog(err);
          });
      } else {
        this.consoleLog("Keep tag");
      }
    },
    deleteCategory(categoryId) {
      if (confirm(this.$t("dash.dictionary.category.confirm"))) {
        neoparentCategories
          .deleteCategory(categoryId)
          .then(result => {
            this.consoleLog(result);
            this.categories = result.data.categories;
          })
          .catch(err => {
            this.consoleLog(err);
          });
      }
    },
    editCategory(category) {
      this.$modal.show(
        {
          template: `
            <div>
              <Button
                class="close"
                @click.native="$modal.hide($parent.name)"
                margin="10px"
                color="#31b9be"
                bgcolor="transparent"
                width="30px"
                padding="0"
                icon="close-blue"
              ></Button>
              <h3>{{ title }}</h3>
              <InputField v-model="newTitle" required placeholder="Titel"></InputField>
              <LoadingButton v-if="loading"></LoadingButton>
              <Button
                v-else
                title="Bewerk categorie"
                shadow="true"
                @click.native="editCategory({newTitle, closeModal: function() {$modal.hide($parent.name)}})"
                margin="1rem 0"
              ></Button>
            </div>
          `,
          components: {
            InputField,
            Button
          },
          data: function() {
            return {
              title: category.title,
              newTitle: category.title,
              loading: false
            };
          },
          props: {
            editCategory: {}
          }
        },
        {
          title: category.title,
          editCategory({ newTitle, closeModal }) {
            neoparentCategories
              .editCategory(category.id, { id: category.id, title: newTitle })
              .then(result => {
                this.consoleLog(result);
                this.$emit("category-edit", { newTitle });
                this.loading = false;
                closeModal();
              })
              .catch(err => {
                this.loading = false;
                this.consoleLog(err);
              });
          }
        },
        {
          height: "auto",
          width: "90%",
          class: "comment"
        }
      );
    }
  }
};
</script>

<style scoped lang="scss">
.field-label {
  font-size: 0.75rem;
  color: #888;
  margin-bottom: 4px;
  letter-spacing: 0.02em;
}

.search-row {
  flex-wrap: wrap;
  row-gap: 8px;
}

.search-field {
  min-width: 0;
}

/* Pink shadow-link buttons stand visually apart from the teal scope chips */
.shadow-link-btn.v-btn--outlined {
  border-color: #E05484 !important;
  color: #E05484 !important;
  text-transform: none;
  letter-spacing: 0.02em;
  font-weight: 500;
}

.shadow-link-btn.v-btn--outlined:hover {
  background-color: #FCE7EE !important;
}

.shadow-links {
  flex-wrap: wrap;
  row-gap: 4px;
}

.scope-row {
  flex-wrap: wrap;
}

/* Inactive scope chips reuse the app's mint border */
.scope-chip.v-chip--outlined {
  border-color: #afdfd3 !important;
  color: #9e9e9e !important;
}

.scope-chip.v-chip--outlined:hover {
  background-color: #D9F0EA !important;
  color: #00A9B3 !important;
}

.category-row {
  display: flex;
  align-items: center;
  width: 100%;
}

.category-title {
  flex: 1 1 auto;
  min-width: 0;
  text-align: left;
}

.category-menu-btn {
  flex-shrink: 0;
}

.listitem-content {
  margin-left: 1rem;
  text-align: left;
  border-left: 1px solid #afdfd3;
}

.listitem-content p {
  line-height: 22px;
  font-size: 14px;
  margin: 0;
  padding: 1rem;
  word-break: break-word;
}

.entry-content {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.entry-actions {
  display: flex;
  flex-direction: column;
  margin-right: 1rem;
  padding: 1rem 0;
}

.tags {
  display: flex;
  flex-wrap: wrap;
  padding: 1rem;
}

.upload-container {
  width: 100%;
  overflow: hidden;
}

.upload-container img,
.upload-container video {
  max-width: 100%;
  margin: 0 auto;
  padding: 0 1rem;
}

/* Phone: stack the shadow links under the search input so nothing overflows */
@media (max-width: 600px) {
  .shadow-links {
    width: 100%;
    margin-left: 0 !important;
    justify-content: flex-start;
  }
  .shadow-link-btn {
    flex: 1 1 auto;
  }
}
</style>
